import { API_CONFIG } from '../config/api';
import { useFunnelV3Store } from '../store/funnelV3Store';
import type { FunnelV3Data, HeightUnit, WeightUnit } from '../store/funnelV3Store';

/**
 * Quiz answers live in a device-local store, so an abandoned-cart lead who opens
 * the email in a different browser reaches the paywall with nothing filled in —
 * and the projection chart falls back to invented weights (80kg → 65kg) rather
 * than showing nothing. Every answer was already sent to the profile at lead
 * capture, so read it back and fill the blanks.
 *
 * Blanks only. A quiz freshly completed on this device always wins over the
 * server copy, which may be months stale.
 */

const KG_TO_LB = 2.205;
const HYDRATION_TIMEOUT_MS = 3000;

/** The subset of GET /users/profile that maps onto quiz answers. */
interface ProfileQuizPayload {
    age?: number | null;
    height?: number | null;
    heightUnit?: string | null;
    weight?: number | null;
    originWeight?: number | null;
    weightGoal?: number | null;
    weightUnit?: string | null;
    targetDate?: string | null;
    firstName?: string | null;
    primaryGoal?: string | null;
    mainBarrier?: string | null;
    rewardPreference?: string | null;
    motivation?: string | null;
    activityLevel?: string | null;
    walkingHistory?: string | null;
    commitment?: string | null;
    sleepQuality?: string | null;
    physicalBarriers?: string[] | null;
}

const round1 = (value: number) => Math.round(value * 10) / 10;

/** The API stores whatever number the member typed; weightUnit says which unit that was. */
const toKg = (value: number, apiWeightUnit: WeightUnit) =>
    apiWeightUnit === 'lbs' ? round1(value / KG_TO_LB) : round1(value);

const toFunnelWeightUnit = (apiWeightUnit?: string | null): WeightUnit =>
    apiWeightUnit === 'lb' || apiWeightUnit === 'lbs' ? 'lbs' : 'kg';

const toFunnelHeightUnit = (apiHeightUnit?: string | null): HeightUnit =>
    apiHeightUnit === 'ft' || apiHeightUnit === 'in' ? 'ft' : 'cm';

/** Buckets match the GoalWeight/BodyComparison age ranges; the API only keeps a raw age. */
const ageToAgeGroup = (age: number): string | null => {
    if (!Number.isFinite(age) || age < 18) return null;
    if (age <= 29) return '18-29';
    if (age <= 39) return '30-39';
    if (age <= 49) return '40-49';
    if (age <= 59) return '50-59';
    return '60+';
};

/** The chart parses targetDate as `${targetDate}T00:00:00`, so it needs a bare date. */
const toIsoDate = (value?: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10);
};

export function quizFieldsFromProfile(profile: ProfileQuizPayload): Partial<FunnelV3Data> {
    const fields: Partial<FunnelV3Data> = {};

    const weightUnit = toFunnelWeightUnit(profile.weightUnit);
    const currentWeight = profile.weight ?? profile.originWeight;
    if (typeof currentWeight === 'number' && currentWeight > 0) {
        fields.currentWeightKg = toKg(currentWeight, weightUnit);
        fields.weightUnit = weightUnit;
    }
    if (typeof profile.weightGoal === 'number' && profile.weightGoal > 0) {
        fields.goalWeightKg = toKg(profile.weightGoal, weightUnit);
        fields.weightUnit = weightUnit;
    }

    // Height is written to the API in cm regardless of the display unit.
    if (typeof profile.height === 'number' && profile.height > 0) {
        fields.heightCm = profile.height;
        fields.heightUnit = toFunnelHeightUnit(profile.heightUnit);
    }

    if (typeof profile.age === 'number' && profile.age > 0) {
        fields.birthYear = new Date().getFullYear() - profile.age;
        const ageGroup = ageToAgeGroup(profile.age);
        if (ageGroup) fields.ageGroup = ageGroup;
    }

    const targetDate = toIsoDate(profile.targetDate);
    if (targetDate) fields.targetDate = targetDate;

    if (profile.firstName) fields.firstName = profile.firstName;
    if (profile.primaryGoal) fields.primaryGoal = profile.primaryGoal;
    if (profile.mainBarrier) fields.mainBarrier = profile.mainBarrier;
    if (profile.rewardPreference) fields.rewardPreference = profile.rewardPreference;
    if (profile.motivation) fields.motivation = profile.motivation;
    if (profile.activityLevel) fields.activityLevel = profile.activityLevel;
    if (profile.walkingHistory) fields.walkingHistory = profile.walkingHistory;
    if (profile.commitment) fields.commitment = profile.commitment;
    if (profile.sleepQuality) fields.sleepQuality = profile.sleepQuality;
    if (profile.physicalBarriers?.length) fields.physicalBarriers = profile.physicalBarriers;

    return fields;
}

/**
 * Units ride along with the measurement they describe — they default to a
 * non-empty value ('kg'/'ft'), so a blank check would never apply them.
 */
const isBlank = (value: FunnelV3Data[keyof FunnelV3Data]): boolean =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

export function blanksFilledByProfile(
    current: FunnelV3Data,
    fromProfile: Partial<FunnelV3Data>,
): Partial<FunnelV3Data> {
    const fills: Partial<FunnelV3Data> = {};

    (Object.keys(fromProfile) as (keyof FunnelV3Data)[]).forEach((field) => {
        if (field === 'weightUnit' || field === 'heightUnit') return;
        if (!isBlank(current[field])) return;
        Object.assign(fills, { [field]: fromProfile[field] });
    });

    if (fills.currentWeightKg !== undefined || fills.goalWeightKg !== undefined) {
        if (fromProfile.weightUnit) fills.weightUnit = fromProfile.weightUnit;
    }
    if (fills.heightCm !== undefined && fromProfile.heightUnit) {
        fills.heightUnit = fromProfile.heightUnit;
    }

    return fills;
}

/**
 * Fills any quiz answer this device is missing from the member's saved profile.
 * Resolves once the store is up to date so callers can hold off rendering the
 * paywall until it shows real numbers. Never throws — a failed hydration just
 * leaves the funnel exactly as it was.
 */
export async function hydrateQuizFromProfile(): Promise<boolean> {
    let token: string | null = null;
    try {
        token = localStorage.getItem('accessToken');
    } catch { /* storage disabled */ }
    if (!token) return false;

    // Callers await this before showing the paywall, so a stalled request must
    // not strand the visitor on the quiz.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), HYDRATION_TIMEOUT_MS);

    let profile: ProfileQuizPayload | null = null;
    try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/users/profile`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
        });
        if (!res.ok) return false;
        const json = await res.json();
        profile = json?.data?.user ?? null;
    } catch {
        return false;
    } finally {
        clearTimeout(timeout);
    }
    if (!profile) return false;

    const { data, updateField } = useFunnelV3Store.getState();
    const fills = blanksFilledByProfile(data, quizFieldsFromProfile(profile));
    const filled = Object.keys(fills) as (keyof FunnelV3Data)[];
    if (filled.length === 0) return false;

    filled.forEach((field) => {
        updateField(field, fills[field] as FunnelV3Data[typeof field]);
    });
    return true;
}
